import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { colors } from '../styles/GlobalStyles';
import { Button } from '../components/Button';
import { Input, Label, FormGroup, ErrorMessage } from '../components/Input';
import { authService } from '../services/authService';
import { toast } from 'react-toastify';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: ${colors.background};
`;

const Card = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 20px ${colors.shadow};
  max-width: 500px;
  width: 100%;
`;

const Title = styled.h2`
  color: ${colors.primary};
  margin-bottom: 10px;
  text-align: center;
  font-size: 28px;
`;

const Description = styled.p`
  color: ${colors.textLight};
  margin-bottom: 30px;
  text-align: center;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  background-color: ${colors.success}15;
  color: ${colors.success};
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  text-align: center;
  font-size: 14px;
`;

const BackLink = styled.a`
  display: block;
  text-align: center;
  margin-top: 20px;
  color: ${colors.primary};
  text-decoration: underline;
  font-size: 14px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

export const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  
  // Try to get token from query parameter first, then from URL path
  const token = searchParams.get('token') || params.token;

  useEffect(() => {
    // Check if token exists
    if (!token) {
      setTokenValid(false);
      setError('Geçersiz veya eksik şifre sıfırlama linki. Lütfen yeni bir şifre sıfırlama talebi oluşturun.');
      toast.error('Geçersiz şifre sıfırlama linki');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (!token) {
      setError('Geçersiz veya eksik token');
      return;
    }

    if (!password || !confirmPassword) {
      setError('Lütfen tüm alanları doldurun');
      return;
    }

    if (password !== confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Resetting password with token:', token ? 'Token exists' : 'No token');
      const response = await authService.resetPassword(token, password);
      console.log('Reset password response:', response);
      
      setSuccess(true);
      toast.success('Şifreniz başarıyla güncellendi!');
      
      // Redirect to home after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      console.error('Reset password error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Şifre sıfırlanamadı. Lütfen tekrar deneyin.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // If no token, show error message
  if (!tokenValid || !token) {
    return (
      <Container>
        <Card>
          <Title>Geçersiz Link</Title>
          <Description>
            Bu şifre sıfırlama linki geçersiz veya süresi dolmuş.
          </Description>
          <ErrorMessage style={{ textAlign: 'center', marginBottom: '20px' }}>
            {error || 'Lütfen yeni bir şifre sıfırlama talebi oluşturun.'}
          </ErrorMessage>
          <Button 
            fullWidth 
            onClick={() => navigate('/forgot-password')}
          >
            Yeni Şifre Sıfırlama Talebi Oluştur
          </Button>
          <BackLink onClick={() => navigate('/')}>
            Ana Sayfaya Dön
          </BackLink>
        </Card>
      </Container>
    );
  }

  // If success, show success message
  if (success) {
    return (
      <Container>
        <Card>
          <Title>Başarılı!</Title>
          <SuccessMessage>
            Şifreniz başarıyla güncellendi. Ana sayfaya yönlendiriliyorsunuz...
          </SuccessMessage>
          <Button 
            fullWidth 
            onClick={() => navigate('/')}
          >
            Ana Sayfaya Git
          </Button>
        </Card>
      </Container>
    );
  }

  // Show reset password form
  return (
    <Container>
      <Card>
        <Title>Yeni Şifre Oluştur</Title>
        <Description>
          Lütfen yeni şifrenizi girin. Şifreniz en az 6 karakter olmalıdır.
        </Description>

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="password">Yeni Şifre</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Yeni şifrenizi girin"
              disabled={loading}
              minLength={6}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Yeni Şifre (Tekrar)</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Yeni şifrenizi tekrar girin"
              disabled={loading}
              minLength={6}
            />
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Button 
            type="submit" 
            fullWidth 
            disabled={loading || !token}
            style={{ marginTop: '10px' }}
          >
            {loading ? 'Güncelleniyor...' : 'Şifremi Güncelle'}
          </Button>
        </form>

        <BackLink onClick={() => navigate('/')}>
          Ana Sayfaya Dön
        </BackLink>
      </Card>
    </Container>
  );
};
